import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import AddressSheet from "../components/AddressSheet";
import BottomNav from "../components/BottomNav";
import ProductCustomizer from "../components/ProductCustomizer";
import { useAppState } from "../context/AppState";
import { api } from "../lib/api";
import { money } from "../lib/orderMath";
import yapeLogo from "../assets/yape-logo.jpg";
import { useLocale } from "../context/Locale";

export default function YapePromosPage() {
  const { t } = useLocale();
  const { addToCart, address, cartCount, openAddress } = useAppState();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customizing, setCustomizing] = useState(null);

  useEffect(() => {
    let active = true;
    api("/yape-promos")
      .then((response) => {
        if (active) setItems(Array.isArray(response?.items) ? response.items : []);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const visibleItems = useMemo(() => {
    const search = query.trim().toLocaleLowerCase("es");
    if (!search) return items;
    return items.filter((item) => `${item.name} ${item.desc || ""} ${item.restaurant?.name || ""}`
      .toLocaleLowerCase("es").includes(search));
  }, [items, query]);

  function addItem(item) {
    const restaurant = item.restaurant;
    if (!restaurant) return;
    if ((item.addon_categories || []).length > 0) {
      setCustomizing({ item, restaurant });
      return;
    }
    addToCart({ ...item, is_yape_promo: 1 }, restaurant, [], 1);
  }

  return (
    <main className="food-app yape-promos-page">
      <AppHeader />
      <div className="food-app__content">
        <header className="yape-promos-hero">
          <Link to="/food" aria-label={t("back")}>‹</Link>
          <div><small>{t("exclusiveOffers")}</small><h1>{t("yapePromos")}</h1><p>{t("yapeSpecialPrices")}</p></div>
          <img className="yape-promos-hero__logo" src={yapeLogo} alt="Yape" />
        </header>

        {!address && (
          <button className="yape-promos-location" type="button" onClick={openAddress}>
            <span>📍</span><div><b>{t("chooseLocation")}</b><small>{t("yapeLocationHelp")}</small></div><i>›</i>
          </button>
        )}

        <label className="food-search yape-promos-search">
          <span aria-hidden="true">⌕</span>
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("searchPromoOrBusiness")} />
        </label>

        {loading && <div className="explore-loading"><i /><span>{t("loadingPromotions")}</span></div>}
        {!loading && error && <div className="food-message food-message--error"><b>{t("promotionsLoadError")}</b><span>{error}</span></div>}
        {!loading && !error && visibleItems.length === 0 && (
          <div className="food-message"><b>{t("noYapePromos")}</b><span>{t("comeBackForOffers")}</span></div>
        )}

        <section className="yape-promos-grid" aria-label={t("availablePromotions")}>
          {visibleItems.map((item) => (
            <article key={item.id}>
              <div className="yape-promos-card__image">
                <img src={item.image || item.restaurant?.image} alt={item.name} loading="lazy" />
                <span>{t("yapeOnly")}</span>
              </div>
              <div className="yape-promos-card__body">
                <small>{item.restaurant?.name}</small>
                <h2>{item.name}</h2>
                {item.desc && <p>{String(item.desc).replace(/<[^>]*>/g, " ")}</p>}
                <div><b>{money(item.price)}</b>{Number(item.old_price) > Number(item.price) && <del>{money(item.old_price)}</del>}</div>
                <button type="button" onClick={() => addItem(item)}>{t("addProduct")}</button>
              </div>
            </article>
          ))}
        </section>
      </div>
      {cartCount > 0 && <Link className="yape-promos-cart" to="/cart">{t("viewMyOrder")} <b>{cartCount}</b></Link>}
      <BottomNav />
      <AddressSheet />
      {customizing && (
        <ProductCustomizer
          itemId={customizing.item.id}
          restaurant={customizing.restaurant}
          onClose={() => setCustomizing(null)}
          onConfirm={(item, restaurant, addons, quantity) => addToCart({ ...item, is_yape_promo: 1 }, restaurant, addons, quantity)}
        />
      )}
    </main>
  );
}
